import { useState } from 'react';
import { Cog6ToothIcon } from '@heroicons/react/24/solid';
import { deleteAppAboutUsItem, updateAppAboutUsItem } from '../../api/new-api';
import { ErrorAlert } from '../../components/alerts';
import { useColors } from '../../provider/colors-provider';

const AboutUsLinkContainer = ({
  val,
  triggerUpdate,
}: {
  val: {
    displayText: string;
    s3ImageKey: string;
    attachmentType: 'WEB_URL' | 'FILE_UPLOAD';
    externalLinkUrl: string;
    mobileAboutItemId: string;
  };
  triggerUpdate: () => void;
}) => {
  const { primary } = useColors();
  const [openSettings, setOpenSettings] = useState(false);

  const [updatedData, setUpdatedData] = useState({
    displayText: val.displayText,
    s3ImageKey: val.s3ImageKey,
    attachmentType: val.attachmentType,
    externalLinkUrl: val.externalLinkUrl,
    mobileAboutItemId: val.mobileAboutItemId,
  });

  const handleUpdate = async () => {
    try {
      await updateAppAboutUsItem({
        aboutUsItemId: val.mobileAboutItemId,
        newItemDisplayText: updatedData.displayText,
        newItemUrl: updatedData.externalLinkUrl,
      });
      setOpenSettings(!openSettings);
    } catch (error: any) {
      console.error('Error updating mobile about us item', error.message);
    } finally {
      triggerUpdate();
    }
  };

  const handleDelete = async (aboutUsItemId: string) => {
    try {
      await deleteAppAboutUsItem({ aboutUsItemId });
    } catch (error: any) {
      console.error('Error deleting mobile about us item', error.message);
    } finally {
      triggerUpdate();
    }
  };

  return (
    <div className="max-w-sm h-max bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
      <div className="p-5 flex flex-row justify-between items-start">
        <div className="flex-1 min-w-0">
          <p className="mb-3 text-lg font-normal text-gray-700 dark:text-gray-400 truncate">
            {val.displayText}
          </p>
          <a
            href={`${
              val.attachmentType === 'FILE_UPLOAD'
                ? val.s3ImageKey
                : val.externalLinkUrl
            }`}
            target="_blank"
            className="block"
          >
            <p className="text-sm italic font-light text-blue-700 dark:text-gray-400">
              open external link
            </p>
          </a>
        </div>
        <div className="flex-shrink-0 ml-4">
          <Cog6ToothIcon
            onClick={() => setOpenSettings(!openSettings)}
            className="w-8 h-8"
            color={primary}
          />
        </div>
      </div>
      {openSettings && (
        <div className="p-2 my-2 flex flex-col gap-2">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-400">
            Display Text
          </label>
          <input
            defaultValue={updatedData.displayText}
            onChange={(e) => {
              setUpdatedData({ ...updatedData, displayText: e.target.value });
            }}
            type="text"
            className="border border-gray-200 rounded-md p-1 w-full"
            placeholder="Display text"
          />

          {val.attachmentType == 'WEB_URL' && (
            <>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-400">
                External Link Url
              </label>
              <input
                defaultValue={updatedData.externalLinkUrl}
                onChange={(e) => {
                  setUpdatedData({
                    ...updatedData,
                    externalLinkUrl: e.target.value,
                  });
                }}
                type="text"
                className="border border-gray-200 rounded-md p-1 w-full"
                placeholder="Link Url"
              />
            </>
          )}
        </div>
      )}

      {openSettings && (
        <div className="p-2 my-2 flex flex-row gap-2 justify-between">
          <button
            onClick={handleUpdate}
            type="button"
            className="rounded-md bg-primary-900 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
            style={{
              background: primary,
            }}
          >
            {'Update'}
          </button>

          <button
            onClick={() => {
              setOpenSettings(!openSettings);
            }}
            type="button"
            className="rounded-md bg-gray-50 px-2 py-1 text-sm font-semibold text-gray-600 shadow-sm hover:bg-gray-100"
          >
            Close
          </button>
          <button
            onClick={() => {
              const confirmDelete = window.confirm(
                'Are you sure you want to delete this link?'
              );
              if (confirmDelete) handleDelete(val.mobileAboutItemId);
            }}
            type="button"
            className="rounded-md bg-red-50 px-2 py-1 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-100"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default AboutUsLinkContainer;
